import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useSeo } from "@/hooks/useSeo";
import { players } from "@/mocks/players";
import { realTeams, getTeamById } from "@/utils/nba";
import type { Conference } from "@/types";
import TeamBadge from "@/components/base/TeamBadge";

export default function Teams() {
  const [conference, setConference] = useState<Conference | "all">("all");
  const [keyword, setKeyword] = useState("");

  useSeo({
    title: "NBA球队与球员资料库 - NBA直播吧",
    description: "NBA 全部 30 支球队的资料库，包含球队徽标、所在赛区、战绩与阵容名单，并可直接跳转查看球员详细数据。",
    keywords: "NBA球队,NBA球员资料,NBA球队名单,NBA阵容,NBA直播吧",
  });

  const filteredTeams = useMemo(() => {
    const kw = keyword.trim();
    return realTeams().filter((team) => {
      if (conference !== "all" && team.conference !== conference) return false;
      if (kw && !team.fullName.includes(kw) && !team.abbreviation.toLowerCase().includes(kw.toLowerCase())) return false;
      return true;
    });
  }, [conference, keyword]);

  const hotPlayers = players.slice(0, 12);

  return (
    <div className="mx-auto max-w-page px-4 py-8 md:px-6 md:py-10">
      <nav className="mb-5 flex items-center gap-2 text-sm text-foreground-500">
        <Link to="/" className="hover:text-primary-700 cursor-pointer">首页</Link>
        <i className="ri-arrow-right-s-line" />
        <span className="text-foreground-900 font-medium">球队与球员</span>
      </nav>

      <div className="mb-6">
        <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">NBA 球队与球员资料</h1>
        <p className="mt-2 text-sm text-foreground-600">
          浏览全部 30 支球队的基本资料、赛区与历史战绩，点击任意球队查看阵容与近期比赛结果。
        </p>
      </div>

      <div className="mb-6 flex flex-wrap items-center gap-3">
        <div className="flex flex-wrap gap-1 rounded-md bg-background-100 p-1">
          {([{ value: "all", label: "全部" }, { value: "East", label: "东部" }, { value: "West", label: "西部" }] as const).map((item) => (
            <button
              key={item.value}
              type="button"
              onClick={() => setConference(item.value)}
              className={`px-4 py-2 text-sm rounded-md transition-colors whitespace-nowrap cursor-pointer ${conference === item.value ? "bg-primary-500 text-background-50 font-medium" : "text-foreground-600 hover:bg-background-200"}`}
            >
              {item.label}
            </button>
          ))}
        </div>
        <input
          value={keyword}
          onChange={(event) => setKeyword(event.target.value)}
          type="text"
          placeholder="搜索球队名称或缩写"
          className="w-full rounded-md border border-background-300 bg-background-50 px-3 py-2 text-sm text-foreground-900 outline-none focus:border-primary-400 sm:w-64"
        />
      </div>

      {filteredTeams.length > 0 ? (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {filteredTeams.map((team) => (
            <Link
              key={team.id}
              to={`/team/${team.id}`}
              className="group flex items-center gap-4 rounded-lg border border-background-200 bg-background-50 p-4 transition-all hover:border-primary-300 cursor-pointer"
            >
              <TeamBadge team={team} size="md" />
              <div className="min-w-0 flex-1">
                <p className="truncate font-heading text-base font-semibold text-foreground-950 group-hover:text-primary-700">{team.fullName}</p>
                <p className="text-xs text-foreground-500">{team.division} · {team.conference === "East" ? "东部" : "西部"}</p>
                <p className="mt-1 text-xs text-foreground-600 font-medium">{team.wins} 胜 {team.losses} 负</p>
              </div>
              <i className="ri-arrow-right-s-line text-xl text-foreground-300 group-hover:text-primary-500" />
            </Link>
          ))}
        </div>
      ) : (
        <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-50 py-16 text-center">
          <span className="mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-background-100 text-foreground-400">
            <i className="ri-search-line text-2xl" />
          </span>
          <p className="font-heading text-lg font-semibold text-foreground-900">没有找到匹配的球队</p>
          <button
            type="button"
            onClick={() => { setConference("all"); setKeyword(""); }}
            className="mt-6 rounded-md bg-primary-500 px-6 py-2.5 text-sm font-semibold text-background-50 hover:bg-primary-600 whitespace-nowrap cursor-pointer"
          >
            重置筛选
          </button>
        </div>
      )}

      <section className="mt-12">
        <h2 className="mb-5 font-heading text-xl md:text-2xl font-bold text-foreground-950">热门球员</h2>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
          {hotPlayers.map((player) => {
            const team = getTeamById(player.teamId);
            return (
              <Link
                key={player.id}
                to={`/player/${player.id}`}
                className="flex items-center gap-3 rounded-lg border border-background-200 bg-background-50 p-3 transition-all hover:border-primary-300 cursor-pointer"
              >
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-background-100 text-foreground-600">
                  <i className="ri-user-line" />
                </span>
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium text-foreground-950">{player.name}</p>
                  <p className="truncate text-xs text-foreground-500">{team?.abbreviation} · {player.position}</p>
                </div>
              </Link>
            );
          })}
        </div>
      </section>
    </div>
  );
}